import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { type BaseQLSelectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { HTML } from '@adornis/cms/db/scalars.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { and } from '@adornis/validation/functions/and.js';
import { minLength } from '@adornis/validation/functions/length.js';
import { min } from '@adornis/validation/functions/range.js';
import { ColorDefinitions } from './ColorDefinitions.js';

function getScope(setting: Setting) {
  if (setting.parentInstitutionID) return 'workshop';
  if (setting.institutionID) return 'institution';
  return 'global';
}

export async function getInheritedSetting(
  setting: Setting,
  propName: keyof Setting,
): Promise<[string, any] | undefined> {
  let parent = await getParent(setting);
  while (parent) {
    const value = parent[propName];
    const scope = getScope(parent);
    if (value) return [scope, value];
    else parent = await getParent(parent);
  }
}

function createInheritedPropsResolver(propName: keyof Setting) {
  return function (this: Setting) {
    return () => getInheritedSetting(this, propName);
  };
}

function createInheritedPropsOptions(propName: keyof Setting) {
  return {
    resolve: createInheritedPropsResolver(propName),
  };
}

const settingFieldDependencies = {
  classes: () => [Setting],
  ids: async function (this: Setting) {
    const scope = getScope(this);
    if (scope === 'global') return [];
    if (scope === 'institution') return getParent(this).then(parent => (parent ? [parent._id] : []));

    const parent = await getParent(this);
    if (!parent) return [];
    const parent2 = await getParent(parent);
    if (!parent2) return [parent._id];
    return [parent._id, parent2._id];
  },
};

async function getParent(setting: Setting) {
  if (setting.parentInstitutionID) {
    const parentSetting = (await loadMongoData(Setting, { institutionID: setting.parentInstitutionID }).then(
      it => it[0],
    )) as Setting;
    return parentSetting;
  }

  if (setting.institutionID) {
    return Setting.getGlobalSettings()({ ...Setting.allFields, colorDefinitions: ColorDefinitions.allFields });
  }

  return undefined;
}

@Entity()
export class SettingColorDefintionProp extends AdornisEntity {
  static override _class = 'SettingColorDefintionProp';

  @Field(type => String)
  scope!: string;

  @Field(type => ColorDefinitions)
  value: Maybe<ColorDefinitions>;
}

@Entity()
export class Setting extends MongoEntity {
  static override _class = 'Setting';
  static override _collectionName = 'settings';

  @Field(type => String)
  productID: Maybe<string>;

  @Field(type => String)
  parentInstitutionID: Maybe<string>;

  @Field(type => String)
  institutionID: Maybe<string>;

  @Field(type => HTML)
  courseLocation: Maybe<string>;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, HTML], createInheritedPropsOptions('courseLocation'))
  inheritedCourseLocation?: [string, string];

  @validate(min(1))
  @Field(type => Int)
  capacity?: Maybe<number>;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, Int], createInheritedPropsOptions('capacity'))
  inheritedCapacity?: [string, number];

  @validate(
    and(min(1), options => {
      const firstValue = options.value;
      const secondValue =
        options.target.secondReminderLeadTime ?? parseInt(options.target.inheritedSecondReminderLeadTime?.[1]);
      if (!(typeof firstValue === 'number')) return;
      if (!(typeof secondValue === 'number')) return;
      if (firstValue <= secondValue)
        throw new ValidationError('Erste Erinnerung muss vor der zweiten liegen', {
          translationKey: 'firstReminderBeforeSecond',
          key: options.key,
        });
    }),
  )
  @Field(type => Int)
  firstReminderLeadTime?: Maybe<number>;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, Int], createInheritedPropsOptions('firstReminderLeadTime'))
  inheritedFirstReminderLeadTime?: [string, number];

  @validate(
    and(min(1), options => {
      const secondValue = options.value;
      const firstValue =
        options.target.firstReminderLeadTime ?? parseInt(options.target.inheritedFirstReminderLeadTime?.[1]);
      if (!(typeof firstValue === 'number')) return;
      if (!(typeof secondValue === 'number')) return;
      if (secondValue >= firstValue)
        throw new ValidationError('Erste Erinnerung muss nach der zweiten liegen', {
          translationKey: 'secondReminderAfterFirst',
          key: options.key,
        });
    }),
  )
  @Field(type => Int)
  secondReminderLeadTime: Maybe<number>;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, Int], createInheritedPropsOptions('secondReminderLeadTime'))
  inheritedSecondReminderLeadTime?: [string, number];

  @validate(
    and(min(1), options => {
      const firstValue = options.value;
      const secondValue =
        options.target.secondAppointmentReminderLeadTime ??
        parseInt(options.target.inheritedSecondAppointmentReminderLeadTime?.[1]);
      if (!(typeof firstValue === 'number')) return;
      if (!(typeof secondValue === 'number')) return;
      if (firstValue <= secondValue)
        throw new ValidationError('Erste Erinnerung muss vor der zweiten liegen', {
          translationKey: 'firstReminderBeforeSecond',
          key: options.key,
        });
    }),
  )
  @Field(type => Int)
  firstAppointmentReminderLeadTime?: Maybe<number>;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, Int], createInheritedPropsOptions('firstAppointmentReminderLeadTime'))
  inheritedFirstAppointmentReminderLeadTime?: [string, number];

  @validate(
    and(min(1), options => {
      const secondValue = options.value;
      const firstValue =
        options.target.firstAppointmentReminderLeadTime ??
        parseInt(options.target.inheritedFirstAppointmentReminderLeadTime?.[1]);
      if (!(typeof firstValue === 'number')) return;
      if (!(typeof secondValue === 'number')) return;
      if (secondValue >= firstValue)
        throw new ValidationError('Erste Erinnerung muss nach der zweiten liegen', {
          translationKey: 'secondReminderAfterFirst',
          key: options.key,
        });
    }),
  )
  @Field(type => Int)
  secondAppointmentReminderLeadTime: Maybe<number>;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, Int], createInheritedPropsOptions('secondAppointmentReminderLeadTime'))
  inheritedSecondAppointmentReminderLeadTime?: [string, number];

  @Field(type => Int)
  meetingInfoMailLeadTime: Maybe<number>;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, Int], createInheritedPropsOptions('meetingInfoMailLeadTime'))
  inheritedMeetingInfoMailLeadTime?: [string, number];

  @Field(type => ColorDefinitions)
  colorDefinitions: Maybe<ColorDefinitions>;

  // NOTE: type [string, SettingColorDefintionProp] does currently not work for some reason
  //       feel free to use tuple again when engine is fixed
  @Dependencies(settingFieldDependencies)
  @Field(type => SettingColorDefintionProp, {
    resolve: function (this: Setting) {
      return async () => {
        const res = await getInheritedSetting(this, 'colorDefinitions');
        if (!res) return null;
        const [scope, value] = res;
        return new SettingColorDefintionProp({ scope, value });
      };
    },
  })
  inheritedColorDefinitions?: SettingColorDefintionProp;

  @Field(type => [String])
  extraParticipationConstraints: Maybe<Array<string>>;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, Int], createInheritedPropsOptions('extraParticipationConstraints'))
  inheritedExtraParticipationConstraints?: [string, Array<string>];

  @validate(min(0))
  @Field(type => Int)
  blockSignOffDaysInAdvance: Maybe<number>;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, Int], createInheritedPropsOptions('blockSignOffDaysInAdvance'))
  inheritedBlockSignOffDaysInAdvance?: [string, number];

  @validate(min(0))
  @Field(type => Int)
  blockAutomaticMoveUpDaysInAdvance: Maybe<number>;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, Int], createInheritedPropsOptions('blockAutomaticMoveUpDaysInAdvance'))
  inheritedBlockAutomaticMoveUpDaysInAdvance?: [string, number];

  @validate(min(1))
  @Field(type => Int)
  qrCodeValidityInMinutes: Maybe<number>;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, Int], createInheritedPropsOptions('qrCodeValidityInMinutes'))
  inheritedQrCodeValidityInMinutes?: [string, number];

  @Field(type => String)
  interAccountingEmail1: Maybe<string>;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, String], createInheritedPropsOptions('interAccountingEmail1'))
  inheritedInterAccountingEmail1?: [string, string];

  @Field(type => String)
  interAccountingEmail2: Maybe<string>;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, String], createInheritedPropsOptions('interAccountingEmail2'))
  inheritedInterAccountingEmail2?: [string, string];

  @Field(type => String)
  interAccountingEmail3: Maybe<string>;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, String], createInheritedPropsOptions('interAccountingEmail3'))
  inheritedInterAccountingEmail3?: [string, string];

  @Field(type => String)
  evaluationLink: Maybe<string>;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, String], createInheritedPropsOptions('evaluationLink'))
  inheritedEvaluationLink?: [string, string];

  @Field(type => String)
  evaluationLinkCode: Maybe<string>;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, String], createInheritedPropsOptions('evaluationLinkCode'))
  inheritedEvaluationLinkCode?: [string, string];

  @Field(type => Int)
  sendInvoiceDaysInAdvance: Maybe<number>;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, Int], createInheritedPropsOptions('sendInvoiceDaysInAdvance'))
  inheritedSendInvoiceDaysInAdvance?: [string, number];

  @validate(options => {
    if (options.value === undefined || options.value === null) void 0; // allow null-values
    else return minLength(1, 'PAYMENT.select_method')(options);
  })
  @Field(type => [String])
  paymentMethods?: string[];

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, [String]], createInheritedPropsOptions('paymentMethods'))
  inheritedPaymentMethods?: [string, string[]];

  @Field(type => String)
  replyToMailAddress: Maybe<string>;

  @Field(type => String)
  infoLink: Maybe<string>;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, String], createInheritedPropsOptions('infoLink'))
  inheritedInfoLink?: [string, string];

  @Field(type => Boolean, { default: v => v ?? true })
  autoNachrueckenEnabled!: boolean;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, Boolean], createInheritedPropsOptions('autoNachrueckenEnabled'))
  inheritedAutoNachrueckenEnabled?: [string, boolean];

  @Field(type => Boolean, { default: v => v ?? false })
  autoSendCertifatesEnabled!: boolean;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, Boolean], createInheritedPropsOptions('autoSendCertifatesEnabled'))
  inheritedAutoSendCertifatesEnabled?: [string, boolean];

  // Automatically move "verbindlich angemeldet" participations to no_show after 7 days
  @Field(type => Boolean, { default: v => v ?? false })
  autoMoveNoShowEnabled!: boolean;

  @Dependencies(settingFieldDependencies)
  @Field(type => [String, Boolean], createInheritedPropsOptions('autoMoveNoShowEnabled'))
  inheritedAutoMoveNoShowEnabled?: [string, boolean];

  @Field(type => Boolean, { default: v => v ?? false })
  allowSubscribingToWaitinglistWithoutMeetingRequirements!: boolean;

  @Dependencies(settingFieldDependencies)
  @Field(
    type => [String, Boolean],
    createInheritedPropsOptions('allowSubscribingToWaitinglistWithoutMeetingRequirements'),
  )
  inheritedAllowSubscribingToWaitinglistWithoutMeetingRequirements?: [string, boolean];

  @Field(type => String, { default: v => v ?? null })
  institutionOverview: Maybe<string>;

  @Query(type => Setting)
  static getNewProductSetting(@Arg('institutionID', type => String) institutionID: string) {
    return async (gqlFields: BaseQLSelectionSet<Setting>) => {
      // try to find unused settings object (to prevent creating lots of unused setting-objects)
      const settingCol = await getCollection(Setting._class);
      // TODO: solve this diffrently. Unused settings should not get created in the first place
      const unusedSettings = await settingCol.find({ parentInstitutionID: institutionID, productID: null }).toArray();
      if (unusedSettings.length > 0) return unusedSettings[0] as Setting;

      const newSetting = new Setting({ parentInstitutionID: institutionID });
      const id = await newSetting.create();
      return (await settingCol.findOne({ _id: id })) as Setting;
    };
  }

  @Query(type => Setting)
  static getProductSetting(
    @Arg('productID', type => String) productID: string,
    @Arg('key', type => String) key: string,
  ) {
    return async () => {
      const productSetting = (await loadMongoData(Setting, { productID })).at(0);
      if (!productSetting) throw new Error('Settings not found for product: ' + productID);
      if (!productSetting[key] !== null && productSetting[key] !== undefined) return productSetting[key];
      logger.info({ productSetting }, 'jjjjjjjjjjjjjjjjjjjj');
      if (!productSetting.parentInstitutionID)
        throw new Error('product Settings has no parent institution: ' + productSetting._id);
      const institutionSetting = (
        await loadMongoData(Setting, { institutionID: productSetting.parentInstitutionID })
      ).at(0);
      if (!institutionSetting)
        throw new Error('Settings not found for institution: ' + productSetting.parentInstitutionID);
      if (!institutionSetting[key] !== null && institutionSetting[key] !== undefined) return institutionSetting[key];

      const globalSetting = await Setting.getGlobalSettings()(gqlFields);
      return globalSetting[key];
    };
  }

  @Query(type => Setting)
  static getGlobalSettings() {
    return async <T extends BaseQLSelectionSet<Setting>>(gqlFields: T): Promise<NarrowedEntity<Setting, T>> => {
      const existingGlobalSettings = await getByID(Setting, '_global_settings')(gqlFields);
      if (existingGlobalSettings) return existingGlobalSettings;

      // create global Settings
      const newGlobalSettings = new Setting({ _id: '_global_settings' });
      await newGlobalSettings.create();
      return newGlobalSettings as NarrowedEntity<Setting, T>;
    };
  }
}
